import { Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import { StepButton, StepTypography } from 'shell/components/shared/step-button';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RegistrationCivilUnionFormVM } from 'mutation/view-models/registration-civil-union';
import { ResponseAnprSaveData, SubjectsList } from 'shell/models/person';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { PmiButton } from 'shell/components/shared/button';
import { HttpResponse } from 'shell/models/http-response';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
// import { checkPersonRegistrationCivilUnion } from 'mutation/utils/transformer-registration-civil-union';
import ExitModal from 'mutation/components/shared/exit-modal';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformerRequestRegistrationCivilUnionData } from 'mutation/utils/transformer-request-registration-civil-union';
import { toast } from 'react-toastify';
import { validationSchema } from 'mutation/constants/registration-civil-union-validation';
import { RegistrationCivilUnionNonResidentCrumbs } from 'mutation/constants/registration-civil-union-non-resident-crumbs';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
// import ErrorModal from 'mutation/components/shared/error-modal';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import PmiTitle from 'shell/components/shared/title';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { transformerRegistrationCivilUnionNonResidentData } from 'mutation/utils/transformer-registration-civil-union-non-resident';
import RegistrationCivilUnionNonResident from 'mutation/components/registration-civil-union/non-resident';

export interface RegistrationCivilUnionNonResidentPageProps {
  isWithoutBreadcrumbs?: boolean;
  onHandleExit?: () => void;
  onConfirm?: () => void;
  initialValues?: RegistrationCivilUnionFormVM;
  disabledFromAct?: boolean;
  goBackUrl?: string;
  editSipoUrl?: string;
}

const RegistrationCivilUnionNonResidentPage = ({
  isWithoutBreadcrumbs = false,
  onHandleExit,
  onConfirm,
  initialValues = {},
  goBackUrl = '/mutazioni/persona/unione-civile/non-residenti',
  editSipoUrl,
}: RegistrationCivilUnionNonResidentPageProps): React.ReactElement => {
  //Modal state
  const [open, setOpen] = useState<boolean>(false);
  // const [openError, setOpenError] = useState<boolean>(false);

  const params = useParams();

  const { idIntestatario, idSchedaSoggettoANPR } = params;

  const [anpr] = useHttpFetch<HttpResponse<SubjectsList>, RegistrationCivilUnionFormVM>(
    `${__PERSON_API_URL__}/non-residenti/soggetto`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          matricolaSipo: idIntestatario || undefined,
          idSchedaSoggettoAnpr: idSchedaSoggettoANPR || undefined,
        },
      },
      transformer: transformerRegistrationCivilUnionNonResidentData,
    }
  );

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [responseCivilUnion, registerCivilUnion] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/non-residenti/registrazione-unione-civile`, {
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedAnprData = responseCivilUnion.data as SaveDataAnpr;

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (
      castedAnprData?.errors?.length &&
      castedAnprData?.errors?.length > 0 &&
      visibility.error === undefined
    ) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const navigate = useNavigate();

  const handleSubmit = useCallback(
    (values) => {
      const data = transformerRequestRegistrationCivilUnionData(values);

      (
        registerCivilUnion({ data }) as ResponsePromise<
          HttpResponse<ResponseAnprSaveData>,
          SaveDataAnpr
        >
      ).then((response) => {
        const castedResponse = response as SaveDataAnpr;

        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }

        if (onConfirm) {
          onConfirm();
          return;
        }

        toast.success('Operazione eseguita con successo');
        navigate(goBackUrl);
      });
    },
    [registerCivilUnion, navigate]
  );

  const handleExit = useCallback(() => {
    navigate(goBackUrl);
  }, [navigate]);

  const handleOpenModal = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  // useEffect(() => {
  //   const data = anpr.data as RegistrationCivilUnionFormVM;

  //   const canBeMutated = checkPersonRegistrationCivilUnion(data?.civilStatus?.oldStatus);

  //   if (!canBeMutated) {
  //     // persona già coniugata
  //     setOpenError(true);
  //   }
  // }, [anpr.data]);

  // const onCloseErrorModal = useEventCallback((value: boolean) => {
  //   setOpenError(value);
  //   navigate(goBackUrl);
  // });

  const isLoading = !anpr.data || anpr.isLoading;

  return !isLoading ? (
    <Fragment>
      {isWithoutBreadcrumbs ? undefined : (
        <Breadcrumbs items={RegistrationCivilUnionNonResidentCrumbs} />
      )}
      <PmiTitle title="Registrazione Unione Civile Non Residenti" />
      <Formik
        validationSchema={validationSchema}
        initialValues={{ ...(anpr.data || {}), ...initialValues } as RegistrationCivilUnionFormVM}
        onSubmit={handleSubmit}
      >
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <StickyDiv>
                <StepButton active fullWidth>
                  <StepTypography active>Stato Civile</StepTypography>
                </StepButton>
                <div className="pmi-pt-4">
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrandThird"
                      variant="outlined"
                      fullWidth
                      type="submit"
                      id="confirm"
                    >
                      Conferma
                    </PmiButton>
                  </div>
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrandThird"
                      variant="outlined"
                      fullWidth
                      type="reset"
                      id="reset"
                    >
                      Ripulisci
                    </PmiButton>
                  </div>
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrand"
                      variant="contained"
                      fullWidth
                      onClick={handleOpenModal}
                      id="deny-operation"
                    >
                      Annulla Operazione
                    </PmiButton>
                  </div>
                </div>
              </StickyDiv>
            </Grid>
            <Grid item xs={12} md={10}>
              <Grid container spacing={2} item xs={12}>
                <Grid item xs={12}>
                  <SearchMessages
                    type={MessageType.Error}
                    title={errorMessage.title || ''}
                    messages={errorMessage.messages || []}
                    visibility={errorMessage.visibility || false}
                    onChangeVisibility={onVisibility}
                  />
                  <SearchMessages
                    type={MessageType.Warning}
                    title={
                      castedAnprData?.warnings?.length === 1
                        ? `C'è 1 warning`
                        : `Ci sono ${castedAnprData?.warnings?.length} warning`
                    }
                    messages={castedAnprData?.warnings || []}
                    visibility={
                      visibility.warning !== undefined
                        ? visibility.warning
                        : (castedAnprData?.warnings || [])?.length > 0
                    }
                    onChangeVisibility={onVisibility}
                  />
                  <RegistrationCivilUnionNonResident editSipoUrl={editSipoUrl} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <ExitModal
            open={open}
            onClose={() => setOpen(false)}
            onConfirm={() => {
              if (onHandleExit) {
                onHandleExit();
                return;
              }
              handleExit();
            }}
          />
          {/* <ErrorModal open={openError} onClose={onCloseErrorModal} /> */}
        </Form>
      </Formik>
    </Fragment>
  ) : (
    <></>
  );
};

RegistrationCivilUnionNonResidentPage.displayName =
  'Registration CivilUnion Non resident page component';

export default RegistrationCivilUnionNonResidentPage;
